/*
 * custom-reviews.js — 独自レビュー表示セクションのクライアント描画(カードデザイン v2)
 *
 * 依存ゼロの IIFE。Cloudflare Worker が返す JSON(schemaVersion 1)を取得し、
 * DOM 契約に従って新カード(アバター/クランプ/続きを読む/画像グリッド+ライトボックス)を描画する。
 *
 * セキュリティ方針:
 *  - title / body / reviewerName / date / アバター文字は必ず textContent で設定(XSS 防御)。
 *  - innerHTML / insertAdjacentHTML / DOMParser は一切使用しない。
 *  - 画像 URL(thumb / full)は new URL() で https のみ許可(Worker は https しか配信しない契約)。
 *
 * ライトボックスは body 直下・ページ共有の単一インスタンス(祖先 transform による
 * position:fixed 破綻と、複数セクション時の多重生成を同時に回避する)。
 * 同じカードの画像グリッド内はグループとして扱い、前後ボタン / ←→キー / 横スワイプで
 * 循環移動できる(1枚のときは前後ボタンとカウンタを hidden にする)。
 *
 * 契約: reports/implementation-plan.md §2(JSON)/§4.2(JS)/§8(既定値)
 *       + 2026-07-21 カードデザイン v2 の DOM 契約(CSS/Liquid ワーカーと共通)
 */
(function () {
  'use strict';

  // 複数セクション配置で <script> が複数出力されても、document への常駐リスナー・
  // 共有ライトボックスが1セットになるよう多重実行をガードする。
  if (window.__customReviewsLoaded) {
    return;
  }
  window.__customReviewsLoaded = true;

  // ---- 定数 --------------------------------------------------------------

  var FETCH_TIMEOUT_MS = 8000;
  var IO_ROOT_MARGIN = '200px';
  var RESIZE_DEBOUNCE_MS = 200;

  // Worker レスポンスのフィールド名(§2.3 と一致)
  var FIELD = {
    id: 'id',
    rating: 'rating',
    title: 'title',
    body: 'body',
    reviewerName: 'reviewerName',
    date: 'date',
    dateISO: 'dateISO',
    type: 'type',
    pinned: 'pinned',
    images: 'images',
    verified: 'verified'
  };

  var MORE_LABEL_OPEN = '続きを読む';
  var MORE_LABEL_CLOSE = '閉じる';
  var MORE_ICON_OPEN = '∨';
  var MORE_ICON_CLOSE = '∧';

  var EDITOR_MSG_ERROR =
    'レビューを取得できませんでした。Worker エンドポイント URL の設定を確認してください。';
  var EDITOR_MSG_EMPTY =
    '表示できる公開レビューがありません(0件)。Worker エンドポイントおよびレビューの公開状態を確認してください。';

  // ---- ユーティリティ ----------------------------------------------------

  /** Shopify テーマエディタのプレビュー中か。 */
  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /** 属性値を整数として読む。無効なら fallback。 */
  function readInt(section, attr, fallback) {
    var n = parseInt(section.getAttribute(attr), 10);
    return (typeof n === 'number' && isFinite(n)) ? n : fallback;
  }

  /** data-cr-type を正規化。無効値は 'both'。 */
  function readType(section) {
    var t = (section.getAttribute('data-cr-type') || '').toLowerCase();
    if (t === 'product' || t === 'store' || t === 'both') {
      return t;
    }
    return 'both';
  }

  /** エンドポイント末尾の '/' を除去。空/未設定は ''。 */
  function normalizeEndpoint(raw) {
    if (!raw) {
      return '';
    }
    return String(raw).trim().replace(/\/+$/, '');
  }

  /** rating を Number 化して 1〜5 にクランプ(小数は保持=平均評価用)。 */
  function clampRating(value) {
    var n = Number(value);
    if (!isFinite(n)) {
      n = 0;
    }
    if (n < 1) {
      n = 1;
    }
    if (n > 5) {
      n = 5;
    }
    return n;
  }

  /** 画像 URL を検証。https の絶対 URL のみ許可、それ以外は null。 */
  function safeImageUrl(url) {
    if (!url || typeof url !== 'string') {
      return null;
    }
    try {
      var u = new URL(url);
      if (u.protocol === 'https:') {
        return u.href;
      }
    } catch (e) {
      // 相対 URL・不正 URL は new URL() が throw
    }
    return null;
  }

  /** reviewerName の先頭1文字(コードポイント単位)。空なら ''。 */
  function firstCodePoint(name) {
    if (!name) {
      return '';
    }
    var arr = Array.from(name);
    return arr.length > 0 ? arr[0] : '';
  }

  /** pinned 優先 → dateISO 降順(both 連結時の再ソート用)。 */
  function byPinnedThenDate(a, b) {
    var pa = a && a[FIELD.pinned] ? 1 : 0;
    var pb = b && b[FIELD.pinned] ? 1 : 0;
    if (pa !== pb) {
      return pb - pa;
    }
    // dateISO は YYYY-MM-DD なので文字列比較で日付順になる
    var da = (a && a[FIELD.dateISO]) || '';
    var db = (b && b[FIELD.dateISO]) || '';
    if (da < db) {
      return 1;
    }
    if (da > db) {
      return -1;
    }
    return 0;
  }

  // ---- データ整形 --------------------------------------------------------

  /**
   * レスポンスから対象レビュー配列を取り出す。
   *  - product / store: Worker のソート済み配列をそのまま(コピー)使用。
   *  - both: productReviews + storeReviews を連結し pinned→date で再ソート。
   */
  function extractReviews(data, type) {
    var product = (data && Array.isArray(data.productReviews)) ? data.productReviews : [];
    var store = (data && Array.isArray(data.storeReviews)) ? data.storeReviews : [];

    if (type === 'product') {
      return product.slice();
    }
    if (type === 'store') {
      return store.slice();
    }
    var merged = product.concat(store);
    merged.sort(byPinnedThenDate);
    return merged;
  }

  /** 星オンリー(タイトル・本文とも空白のみ)のレビューか。トップページでは表示しない。 */
  function isRatingOnly(r) {
    var title = r[FIELD.title] == null ? '' : String(r[FIELD.title]);
    var body = r[FIELD.body] == null ? '' : String(r[FIELD.body]);
    return title.replace(/\s/g, '') === '' && body.replace(/\s/g, '') === '';
  }

  /** min_rating・星オンリー(skipRatingOnly が真のときのみ)で除外し、count 件に絞る。 */
  function selectReviews(list, minRating, count, skipRatingOnly) {
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var r = list[i];
      if (!r) {
        continue;
      }
      if (skipRatingOnly && isRatingOnly(r)) {
        continue;
      }
      if (clampRating(r[FIELD.rating]) >= minRating) {
        out.push(r);
      }
      if (out.length >= count) {
        break;
      }
    }
    return out;
  }

  // ---- ライトボックス(ページ共有の単一インスタンス)----------------------

  // ページ内で1個だけ共有するライトボックスの状態。null なら未生成。
  var lightbox = null;

  // スワイプで前後移動と判定する横移動量の下限(px)。縦移動のほうが大きい場合は
  // ページスクロールとみなして無視する。
  var SWIPE_THRESHOLD_PX = 40;

  /** ライトボックスの必須要素が全て揃っているか(古い DOM の掴み直し判定)。 */
  function isCompleteLightbox(overlay) {
    return !!(overlay &&
      overlay.querySelector('.cr-lightbox__backdrop') &&
      overlay.querySelector('.cr-lightbox__panel') &&
      overlay.querySelector('.cr-lightbox__img') &&
      overlay.querySelector('.cr-lightbox__close') &&
      overlay.querySelector('.cr-lightbox__nav--prev') &&
      overlay.querySelector('.cr-lightbox__nav--next') &&
      overlay.querySelector('.cr-lightbox__counter'));
  }

  /** 前/次ボタンを生成(初期 hidden。2枚以上のときだけ表示する)。 */
  function buildLightboxNav(modifier, label, glyph) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cr-lightbox__nav cr-lightbox__nav--' + modifier;
    btn.setAttribute('aria-label', label);
    btn.hidden = true;
    btn.textContent = glyph;
    return btn;
  }

  /**
   * ライトボックス DOM を新規生成して body 直下へ追加する。
   * 構造(CSS ワーカーと共通の契約):
   *   .cr-lightbox > [backdrop, nav--prev, nav--next, panel(img, close, counter)]
   * prev / next は backdrop と panel の兄弟(画面左右中央に固定配置するため)。
   */
  function createLightboxDom() {
    var overlay = document.createElement('div');
    overlay.className = 'cr-lightbox is-hidden';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'レビュー画像');

    var backdrop = document.createElement('div');
    backdrop.className = 'cr-lightbox__backdrop';
    overlay.appendChild(backdrop);

    overlay.appendChild(buildLightboxNav('prev', '前の画像', '‹'));
    overlay.appendChild(buildLightboxNav('next', '次の画像', '›'));

    var panel = document.createElement('figure');
    panel.className = 'cr-lightbox__panel';

    var img = document.createElement('img');
    img.className = 'cr-lightbox__img';
    img.setAttribute('alt', 'レビュー画像');
    panel.appendChild(img);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'cr-lightbox__close';
    close.setAttribute('aria-label', '閉じる');
    close.textContent = '×';
    panel.appendChild(close);

    var counter = document.createElement('figcaption');
    counter.className = 'cr-lightbox__counter';
    counter.setAttribute('aria-live', 'polite');
    counter.hidden = true;
    panel.appendChild(counter);

    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    return overlay;
  }

  /**
   * body 直下にライトボックス DOM を1個だけ生成(遅延・使い回し)。
   * 生成済み(モジュール状態あり)なら既存を返す。DOM だけ残存している場合は
   * document.querySelector('.cr-lightbox') で検出して再利用するが、prev/next/counter を
   * 持たない旧構造だった場合は取り除いて作り直す(要素欠落のまま参照しないため)。
   */
  function ensureLightbox() {
    if (lightbox) {
      return lightbox;
    }

    var overlay = document.querySelector('.cr-lightbox');
    if (overlay && !isCompleteLightbox(overlay)) {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      overlay = null;
    }
    if (!overlay) {
      overlay = createLightboxDom();
    }

    lightbox = {
      overlay: overlay,
      img: overlay.querySelector('.cr-lightbox__img'),
      close: overlay.querySelector('.cr-lightbox__close'),
      prev: overlay.querySelector('.cr-lightbox__nav--prev'),
      next: overlay.querySelector('.cr-lightbox__nav--next'),
      counter: overlay.querySelector('.cr-lightbox__counter'),
      trigger: null,
      keyHandler: null,
      open: false,
      group: [],
      index: 0
    };

    // 閉じる操作(×ボタン / 背景クリック)・前後移動(ボタン / スワイプ)は
    // overlay 単位で1回だけ束縛する(再利用・多重生成でハンドラが重複しないようガード)。
    if (!overlay.__crBound) {
      overlay.__crBound = true;
      lightbox.close.addEventListener('click', function () {
        closeLightbox();
      });
      var backdropEl = overlay.querySelector('.cr-lightbox__backdrop');
      if (backdropEl) {
        backdropEl.addEventListener('click', function () {
          closeLightbox();
        });
      }
      lightbox.prev.addEventListener('click', function () {
        stepLightbox(-1);
      });
      lightbox.next.addEventListener('click', function () {
        stepLightbox(1);
      });
      bindLightboxSwipe(overlay.querySelector('.cr-lightbox__panel'));
    }

    return lightbox;
  }

  /**
   * パネル上の横スワイプで前後移動する(touchstart / touchend のみ)。
   * preventDefault は使わないためページスクロールを妨げない。
   */
  function bindLightboxSwipe(panel) {
    if (!panel || typeof panel.addEventListener !== 'function') {
      return;
    }
    var startX = 0;
    var startY = 0;
    var tracking = false;

    panel.addEventListener('touchstart', function (e) {
      var t = (e.touches && e.touches.length === 1) ? e.touches[0] : null;
      if (!t) {
        tracking = false; // マルチタッチ(ピンチ等)はスワイプ扱いしない
        return;
      }
      tracking = true;
      startX = t.clientX;
      startY = t.clientY;
    });

    panel.addEventListener('touchend', function (e) {
      if (!tracking) {
        return;
      }
      tracking = false;
      var t = (e.changedTouches && e.changedTouches.length > 0) ? e.changedTouches[0] : null;
      if (!t) {
        return;
      }
      var dx = t.clientX - startX;
      var dy = t.clientY - startY;
      if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) {
        return; // 移動量不足 / 縦スワイプは無視
      }
      stepLightbox(dx < 0 ? 1 : -1); // 左へ払う=次へ
    });
  }

  /**
   * クリックされたサムネイルが属する画像グリッドから、表示対象グループを組み立てる。
   * .cr-card__images を辿れない場合(後方互換)や自身を特定できない場合は単一画像。
   * @returns {{group: string[], index: number}}
   */
  function collectLightboxGroup(triggerBtn, safeUrl) {
    var single = { group: [safeUrl], index: 0 };
    var container = triggerBtn ? closestEl(triggerBtn, '.cr-card__images') : null;
    if (!container || typeof container.querySelectorAll !== 'function') {
      return single;
    }

    var thumbs = container.querySelectorAll('button.cr-card__thumb');
    var group = [];
    var index = -1;
    for (var i = 0; i < thumbs.length; i++) {
      var url = safeImageUrl(thumbs[i].getAttribute('data-full'));
      if (!url) {
        continue;
      }
      if (thumbs[i] === triggerBtn) {
        index = group.length;
      }
      group.push(url);
    }
    if (group.length === 0 || index < 0) {
      return single;
    }
    return { group: group, index: index };
  }

  /** 前後の画像を先読みして切替の体感速度を上げる(失敗は無視)。 */
  function preloadNeighbors(lb) {
    if (typeof Image !== 'function' || !lb.group || lb.group.length < 2) {
      return;
    }
    var total = lb.group.length;
    var offsets = [1, -1];
    for (var k = 0; k < offsets.length; k++) {
      var j = ((lb.index + offsets[k]) % total + total) % total;
      if (j === lb.index) {
        continue;
      }
      try {
        var pre = new Image();
        pre.src = lb.group[j];
      } catch (e) {
        // 先読みは最適化に過ぎないため失敗しても無視する
      }
    }
  }

  /**
   * グループ内の index 番目を表示する。index は循環(最後→最初 / 最初→最後)。
   * 画像 src・alt・カウンタ表示・前後ボタンの表示可否をまとめて更新する。
   */
  function showLightboxImage(index) {
    var lb = lightbox;
    if (!lb || !lb.group || lb.group.length === 0) {
      return;
    }
    var total = lb.group.length;
    var i = ((index % total) + total) % total;
    var multi = total > 1;

    lb.index = i;
    lb.img.src = lb.group[i];
    lb.img.setAttribute('alt', multi ? 'レビュー画像 ' + (i + 1) + ' / ' + total : 'レビュー画像');

    if (lb.counter) {
      lb.counter.textContent = multi ? (i + 1) + ' / ' + total : '';
      lb.counter.hidden = !multi;
    }
    if (lb.prev) {
      lb.prev.hidden = !multi;
    }
    if (lb.next) {
      lb.next.hidden = !multi;
    }

    preloadNeighbors(lb);
  }

  /** 現在位置から delta 枚ぶん移動(2枚以上・表示中のときのみ)。 */
  function stepLightbox(delta) {
    var lb = lightbox;
    if (!lb || !lb.open || !lb.group || lb.group.length < 2) {
      return;
    }
    showLightboxImage(lb.index + delta);
  }

  /** 現在表示中(hidden でない)の操作ボタンを DOM 順に返す。 */
  function lightboxFocusables() {
    var lb = lightbox;
    var out = [];
    if (!lb) {
      return out;
    }
    var candidates = [lb.prev, lb.next, lb.close];
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el && !el.hidden && typeof el.focus === 'function') {
        out.push(el);
      }
    }
    return out;
  }

  /** 表示中の操作ボタン間でフォーカスを循環させる(オーバーレイ外へは出さない)。 */
  function focusLightboxSibling(delta) {
    var items = lightboxFocusables();
    if (items.length === 0) {
      return;
    }
    var current = document.activeElement;
    var pos = -1;
    for (var i = 0; i < items.length; i++) {
      if (items[i] === current) {
        pos = i;
        break;
      }
    }
    var nextPos;
    if (pos < 0) {
      // オーバーレイ外(または hidden 化された要素)にフォーカスがある場合は端へ戻す。
      nextPos = delta > 0 ? 0 : items.length - 1;
    } else {
      nextPos = ((pos + delta) % items.length + items.length) % items.length;
    }
    items[nextPos].focus();
  }

  /** サムネイルクリック: 同じグリッドの画像群をグループ化して共有ライトボックスを開く。 */
  function openLightbox(fullUrl, triggerBtn) {
    var safeUrl = safeImageUrl(fullUrl);
    if (!safeUrl) {
      return;
    }
    var lb = ensureLightbox();

    // 多重オープン対策: 既存の keydown リスナーが残っていれば先に必ず解除し、
    // document 上のリスナーが常に1個だけになるようにする。
    if (lb.keyHandler) {
      document.removeEventListener('keydown', lb.keyHandler);
      lb.keyHandler = null;
    }

    var picked = collectLightboxGroup(triggerBtn, safeUrl);
    lb.group = picked.group;
    lb.index = picked.index;
    lb.trigger = triggerBtn || null;
    lb.overlay.classList.remove('is-hidden');
    lb.open = true;
    document.documentElement.classList.add('cr-no-scroll');

    showLightboxImage(lb.index);

    // Esc で閉じる + ←/→ で前後移動 + Tab/Shift+Tab のフォーカストラップ
    // (いずれも表示中のみ常駐し、閉じたら解除する)。
    lb.keyHandler = function (e) {
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        closeLightbox();
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'Left' || e.keyCode === 37) {
        stepLightbox(-1);
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'Right' || e.keyCode === 39) {
        stepLightbox(1);
        return;
      }
      if (e.key === 'Tab' || e.keyCode === 9) {
        e.preventDefault();
        focusLightboxSibling(e.shiftKey ? -1 : 1);
      }
    };
    document.addEventListener('keydown', lb.keyHandler);

    if (typeof lb.close.focus === 'function') {
      lb.close.focus();
    }
  }

  /** ライトボックスを閉じ、keydown リスナーを解除して元のサムネイルへ focus を戻す。 */
  function closeLightbox() {
    var lb = lightbox;
    if (!lb) {
      return;
    }
    lb.overlay.classList.add('is-hidden');
    lb.open = false;
    document.documentElement.classList.remove('cr-no-scroll');

    if (lb.keyHandler) {
      document.removeEventListener('keydown', lb.keyHandler);
      lb.keyHandler = null;
    }

    // 次に開いたときに前回のグループ・位置が残らないようリセットする。
    lb.group = [];
    lb.index = 0;
    if (lb.counter) {
      lb.counter.textContent = '';
      lb.counter.hidden = true;
    }
    if (lb.prev) {
      lb.prev.hidden = true;
    }
    if (lb.next) {
      lb.next.hidden = true;
    }

    var trigger = lb.trigger;
    lb.trigger = null;
    // focus 復帰先が DOM から取り除かれている場合は復帰をスキップ。
    if (trigger && typeof trigger.focus === 'function' &&
        (typeof document.contains !== 'function' || document.contains(trigger))) {
      trigger.focus();
    }
  }

  /** ライトボックスが開いていれば閉じる(共有インスタンス自体は残す)。 */
  function closeLightboxIfOpen() {
    if (lightbox && lightbox.open) {
      closeLightbox();
    }
  }

  // ---- DOM 生成 ----------------------------------------------------------

  /** 星コンテナ(role=img + aria-label + --cr-rating)を生成。 */
  function buildStars(rating, ariaValue) {
    var stars = document.createElement('span');
    stars.className = 'cr-stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', '5段階評価中 ' + ariaValue);
    stars.style.setProperty('--cr-rating', String(rating));

    var fill = document.createElement('span');
    fill.className = 'cr-stars__fill';
    fill.setAttribute('aria-hidden', 'true');
    stars.appendChild(fill);
    return stars;
  }

  /** 上段(アバター + マスク名 + 日付)を生成。 */
  function buildTop(review) {
    var top = document.createElement('div');
    top.className = 'cr-card__top';

    var name = review[FIELD.reviewerName] != null ? String(review[FIELD.reviewerName]) : '';

    var avatar = document.createElement('span');
    avatar.className = 'cr-card__avatar';
    avatar.setAttribute('aria-hidden', 'true');
    avatar.textContent = firstCodePoint(name);
    top.appendChild(avatar);

    var reviewer = document.createElement('span');
    reviewer.className = 'cr-card__reviewer';
    reviewer.textContent = name;
    top.appendChild(reviewer);

    var time = document.createElement('time');
    time.className = 'cr-card__date';
    time.setAttribute('datetime', review[FIELD.dateISO] != null ? String(review[FIELD.dateISO]) : '');
    time.textContent = review[FIELD.date] != null ? String(review[FIELD.date]) : '';
    top.appendChild(time);

    return top;
  }

  /** 「続きを読む」ボタンを生成(初期 hidden、クランプ検出後に表示)。 */
  function buildMoreButton(card) {
    var moreBtn = document.createElement('button');
    moreBtn.type = 'button';
    moreBtn.className = 'cr-card__more';
    moreBtn.hidden = true;
    moreBtn.setAttribute('aria-expanded', 'false');

    var icon = document.createElement('span');
    icon.className = 'cr-card__more-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = MORE_ICON_OPEN;
    moreBtn.appendChild(icon);

    var label = document.createElement('span');
    label.className = 'cr-card__more-label';
    label.textContent = MORE_LABEL_OPEN;
    moreBtn.appendChild(label);

    moreBtn.addEventListener('click', function () {
      var expanded = card.classList.toggle('cr-card--expanded');
      moreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      label.textContent = expanded ? MORE_LABEL_CLOSE : MORE_LABEL_OPEN;
      icon.textContent = expanded ? MORE_ICON_CLOSE : MORE_ICON_OPEN;
    });

    return moreBtn;
  }

  /**
   * 画像グリッドを生成。images[] の各要素で thumb→img.src / full→data-full。
   * thumb・full 双方が https の場合のみ採用(無効な要素はスキップ)。
   * 有効枚数が 0 なら null を返す(要素自体を生成しない)。
   */
  function buildImages(images) {
    if (!Array.isArray(images) || images.length === 0) {
      return null;
    }

    var grid = document.createElement('div');
    grid.className = 'cr-card__images';
    var added = 0;

    for (var i = 0; i < images.length; i++) {
      var entry = images[i];
      if (!entry) {
        continue;
      }
      var thumb = safeImageUrl(entry.thumb);
      var full = safeImageUrl(entry.full);
      if (!thumb || !full) {
        continue;
      }
      added++;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cr-card__thumb';
      btn.setAttribute('data-full', full);

      var img = document.createElement('img');
      img.className = 'cr-card__thumb-img';
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
      img.setAttribute('alt', 'レビュー画像' + added);
      img.src = thumb;
      btn.appendChild(img);

      btn.addEventListener('click', function () {
        openLightbox(this.getAttribute('data-full'), this);
      });

      grid.appendChild(btn);
    }

    return added > 0 ? grid : null;
  }

  /** 1 レビュー分の <li class="cr-card"> を生成(textContent のみ)。 */
  function buildCard(review, showImages) {
    var li = document.createElement('li');
    li.className = 'cr-card';

    var rating = clampRating(review[FIELD.rating]);

    // 上段(アバター + マスク名 + 日付)
    li.appendChild(buildTop(review));

    // 星
    li.appendChild(buildStars(rating, rating));

    // タイトル(空・空白のみ、または本文と同一の場合は要素自体を生成しない)
    // 本文と同一なのは Judge.me がタイトル未入力時に body を title へ複製して返すため。
    // Worker 未更新の環境でも重複表示を防げるよう、テーマ側でも trim 後一致を判定する。
    var titleText = review[FIELD.title] != null ? String(review[FIELD.title]) : '';
    var bodyText = review[FIELD.body] != null ? String(review[FIELD.body]) : '';
    if (titleText.trim() !== '' && titleText.trim() !== bodyText.trim()) {
      var title = document.createElement('p');
      title.className = 'cr-card__title';
      title.textContent = titleText;
      li.appendChild(title);
    } else if (bodyText.trim() !== '') {
      // タイトルなし(または本文と同一)のときは Judge.me 標準と同様、本文を
      // タイトルとしても表示する(1行クランプ)。--auto は展開時もクランプを
      // 維持し(CSS)、超過判定からも除外する(全文は本文側で読めるため)。
      var autoTitle = document.createElement('p');
      autoTitle.className = 'cr-card__title cr-card__title--auto';
      autoTitle.textContent = bodyText;
      li.appendChild(autoTitle);
    }

    // 本文(3行クランプ。改行は CSS 側で表現)
    var text = document.createElement('p');
    text.className = 'cr-card__text';
    text.textContent = bodyText;
    li.appendChild(text);

    // 続きを読む(クランプ超過時のみ表示。判定は挿入後の rAF で行う)
    li.appendChild(buildMoreButton(li));

    // 画像グリッド(images が1件以上 かつ show_images≠false のとき)
    if (showImages) {
      var imagesEl = buildImages(review[FIELD.images]);
      if (imagesEl) {
        li.appendChild(imagesEl);
      }
    }

    return li;
  }

  // ---- クランプ判定 ------------------------------------------------------

  /**
   * セクション内の各カードについてクランプ超過を判定し、「続きを読む」ボタンの
   * 表示/非表示を更新する。
   *  - 展開中(cr-card--expanded)のカードは閉じる手段を奪わないためスキップ。
   *  - 未展開カードは超過なら hidden 解除、非超過なら hidden 付与(隠す方向も対応)。
   *  - clientHeight === 0(未レイアウト)のカードは判定を保留する。
   */
  function evaluateClamp(section) {
    var grid = section.querySelector('.cr-grid');
    if (!grid) {
      return;
    }
    var cards = grid.querySelectorAll('.cr-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.classList.contains('cr-card--expanded')) {
        continue;
      }
      var moreBtn = card.querySelector('.cr-card__more');
      if (!moreBtn) {
        continue;
      }
      // タイトル(自動タイトル --auto を含む)と本文のどちらかが省略されていればボタンを出す。
      // 自動タイトルは展開時に非表示になり本文が全文表示されるため、判定対象に含めてよい
      // (ユーザー指定 2026-07-24: タイトルが省略された場合も「続きを読む」を表示する)。
      var candidates = [];
      var titleEl = card.querySelector('.cr-card__title');
      var textEl = card.querySelector('.cr-card__text');
      if (titleEl) {
        candidates.push(titleEl);
      }
      if (textEl) {
        candidates.push(textEl);
      }
      if (candidates.length === 0) {
        continue;
      }

      // clientHeight===0 の要素は「未レイアウト」または「中身が空」。測れる要素だけで判定し、
      // どれも測れない(=カード自体が未レイアウト)ときは次回(fonts.ready/resize)に委ねる。
      var measurable = [];
      for (var c = 0; c < candidates.length; c++) {
        if (candidates[c].clientHeight > 0) {
          measurable.push(candidates[c]);
        }
      }
      if (measurable.length === 0) {
        continue;
      }

      var overflow = false;
      for (var m = 0; m < measurable.length; m++) {
        if (measurable[m].scrollHeight > measurable[m].clientHeight + 1) {
          overflow = true;
          break;
        }
      }
      moreBtn.hidden = !overflow;
    }
  }

  /**
   * カード挿入後に1フレーム待ってからクランプ判定を行い、Web フォント適用後にも
   * 再判定する(フォント差し替えで行数が変わりうるため)。
   */
  function scheduleClampCheck(section) {
    var run = function () {
      evaluateClamp(section);
    };

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(run);
    } else {
      run();
    }

    if (document.fonts && document.fonts.ready &&
        typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(function () {
        evaluateClamp(section);
      }).catch(function () {
        // フォント読込に失敗しても初回判定を採用して続行。
      });
    }
  }

  /** resize(デバウンス付き)でクランプを再判定するリスナーをセクションに1回だけ登録。 */
  function setupClampResize(section) {
    if (section.__crResizeHandler) {
      return;
    }
    var timer = null;
    var handler = function () {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(function () {
        timer = null;
        evaluateClamp(section);
      }, RESIZE_DEBOUNCE_MS);
    };
    section.__crResizeHandler = handler;
    window.addEventListener('resize', handler);
  }

  /** セクションの resize リスナーを解除(section:unload 時)。 */
  function teardownClampResize(section) {
    if (section.__crResizeHandler) {
      window.removeEventListener('resize', section.__crResizeHandler);
      section.__crResizeHandler = null;
    }
  }

  // ---- 描画・状態遷移 ----------------------------------------------------

  /** グリッド内(スケルトン含む)を空にする。 */
  function clearGrid(grid) {
    while (grid.firstChild) {
      grid.removeChild(grid.firstChild);
    }
  }

  /** designMode 用の日本語エラー文言をルート末尾に表示。 */
  function showEditorMessage(section, message) {
    var existing = section.querySelector('.cr-editor-error');
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }
    var div = document.createElement('div');
    div.className = 'cr-editor-error';
    div.textContent = message;
    section.appendChild(div);
  }

  /**
   * summary から平均評価ブロックを埋める(存在すれば)。
   * セクションのレビュー種別(readType)に応じて使用フィールドを切替える:
   *  - product → productAverageRating + productReviewCount
   *  - store   → storeAverageRating   + storeReviewCount
   *  - both    → averageRating(全体) + totalCount
   * フォールバック: 選択種別の平均フィールドが undefined(旧形式データ)の場合は
   * 従来の averageRating + totalCount を使う(Worker 再デプロイ前でも壊れない)。
   */
  function renderAverage(section, summary) {
    var averageEl = section.querySelector('.cr-average');
    if (!averageEl || !summary) {
      return;
    }

    var type = readType(section);
    var rawAvg;
    var rawTotal;
    if (type === 'product') {
      rawAvg = summary.productAverageRating;
      rawTotal = summary.productReviewCount;
    } else if (type === 'store') {
      rawAvg = summary.storeAverageRating;
      rawTotal = summary.storeReviewCount;
    } else {
      rawAvg = summary.averageRating;
      rawTotal = summary.totalCount;
    }
    // 旧形式データ(新フィールド未配信)は全体値へフォールバック。
    if (rawAvg === undefined || rawAvg === null) {
      rawAvg = summary.averageRating;
      rawTotal = summary.totalCount;
    }

    var avg = Number(rawAvg);
    var total = Number(rawTotal);
    if (!isFinite(avg) || avg <= 0 || !isFinite(total) || total <= 0) {
      return;
    }

    var stars = averageEl.querySelector('.cr-stars');
    if (stars) {
      stars.style.setProperty('--cr-rating', String(clampRating(avg)));
      // a11y: 平均評価の読み上げ値も更新(契約の必須項目ではない付加対応)
      stars.setAttribute('aria-label', '5段階評価中 ' + avg.toFixed(1));
    }
    var textEl = averageEl.querySelector('.cr-average__text');
    if (textEl) {
      textEl.textContent = avg.toFixed(1) + ' (' + total + '件)';
    }
    averageEl.classList.remove('is-hidden');
  }

  /** 成功時: スケルトンをカードで置き換える。 */
  function renderSuccess(section, reviews, summary, showImages) {
    var grid = section.querySelector('.cr-grid');
    if (grid) {
      clearGrid(grid);
      var frag = document.createDocumentFragment();
      for (var i = 0; i < reviews.length; i++) {
        frag.appendChild(buildCard(reviews[i], showImages));
      }
      grid.appendChild(frag);
      grid.setAttribute('aria-busy', 'false');
      scheduleClampCheck(section);
    }

    renderAverage(section, summary);

    section.classList.remove('cr--loading', 'cr--empty', 'cr--error', 'is-hidden');
  }

  /**
   * 失敗系の状態処理(0件 / エラー)。
   *  - 本番: ルートに .is-hidden を付与しセクションごと非表示。
   *  - designMode: 非表示にせず、日本語文言を表示して設定ミスに気付けるようにする。
   */
  function renderFailure(section, stateClass, editorMessage) {
    var grid = section.querySelector('.cr-grid');
    if (grid) {
      clearGrid(grid);
      grid.setAttribute('aria-busy', 'false');
    }

    section.classList.remove('cr--loading');
    section.classList.add(stateClass);

    if (isDesignMode()) {
      section.classList.remove('is-hidden');
      showEditorMessage(section, editorMessage);
    } else {
      section.classList.add('is-hidden');
    }
  }

  function renderEmpty(section) {
    renderFailure(section, 'cr--empty', EDITOR_MSG_EMPTY);
  }

  function renderError(section) {
    renderFailure(section, 'cr--error', EDITOR_MSG_ERROR);
  }

  /** レスポンス確定後の描画振り分け。 */
  function handleData(section, data) {
    var type = readType(section);
    var count = readInt(section, 'data-cr-count', 3);
    if (count < 1) {
      count = 3;
    }
    var minRating = readInt(section, 'data-cr-min-rating', 1);
    var showImages = section.getAttribute('data-cr-show-images') !== 'false';
    var skipRatingOnly = section.getAttribute('data-cr-skip-rating-only') !== 'false';

    var list = extractReviews(data, type);
    var selected = selectReviews(list, minRating, count, skipRatingOnly);

    if (selected.length === 0) {
      renderEmpty(section);
      return;
    }
    renderSuccess(section, selected, data && data.summary, showImages);
  }

  // ---- 取得 --------------------------------------------------------------

  /** Worker からレビューを取得して描画する(1 セクションにつき 1 回)。 */
  function loadSection(section) {
    if (section.__crFetchStarted) {
      return;
    }
    section.__crFetchStarted = true;

    var endpoint = normalizeEndpoint(section.getAttribute('data-cr-endpoint'));
    if (!endpoint) {
      renderError(section);
      return;
    }

    // fetch 非対応環境では同期 throw でスケルトンが残り続けるため、エラー描画して抜ける。
    if (typeof fetch !== 'function') {
      renderError(section);
      return;
    }

    var type = readType(section);
    var requestType = type === 'both' ? 'all' : type;
    var count = readInt(section, 'data-cr-count', 3);
    if (count < 1) {
      count = 3;
    }
    var minRating = readInt(section, 'data-cr-min-rating', 1);

    // 星オンリー(本文・タイトルなし)の除外はセクション設定で切り替え可能(既定: 除外)。
    // 除外ON のとき skip_empty=1 を付けて Worker 側で除外する。Worker は KV の全件から
    // 除外した上で返すため、最新 100 件が星オンリーで埋まっていても本文付きレビューを
    // 取りこぼさない。未対応(旧)Worker はこのパラメータを無視するだけなので、
    // デプロイ順序に依存しない。min_rating フィルタと、旧 Worker 向けの星オンリー除外
    // (保険)は引き続きクライアント側で適用する。limit は付けず全件(Worker 上限 100 件)
    // 取得し、フィルタ後にクライアントで count 件へ絞る(limit=count だと Worker が先に
    // 絞った後のスキップで表示が count 件未満に欠けるため)。
    var skipRatingOnly = section.getAttribute('data-cr-skip-rating-only') !== 'false';
    var url = endpoint + '/reviews?type=' + encodeURIComponent(requestType) +
      (skipRatingOnly ? '&skip_empty=1' : '');

    var controller = null;
    var timer = null;
    var options = { headers: { Accept: 'application/json' } };

    if (typeof AbortController !== 'undefined') {
      controller = new AbortController();
      options.signal = controller.signal;
      timer = setTimeout(function () {
        controller.abort();
      }, FETCH_TIMEOUT_MS);
    }

    fetch(url, options)
      .then(function (res) {
        if (!res || !res.ok) {
          throw new Error('bad-status');
        }
        return res.json();
      })
      .then(function (data) {
        if (timer) {
          clearTimeout(timer);
        }
        handleData(section, data);
      })
      .catch(function () {
        if (timer) {
          clearTimeout(timer);
        }
        renderError(section);
      });
  }

  // ---- Judge.me「レビューを書く」ボタンの取り込み --------------------------
  //
  // Judge.me 新版ウィジェット(Vue)の「レビューを書く」ボタンを、本セクション下部の
  // .cr-more-wrap 内(もっと見るリンクの左)へ移設する。
  //  - ボタンに onclick 等の属性ハンドラは無く、クリック処理は Vue が保持している。
  //    そのためマークアップを複製しても機能しない。DOM ノードごと移動して再利用する
  //    (appendChild / insertBefore による移動ではイベントリスナーが保持される)。
  //  - ボタンの class 属性は絶対に変更しない(Vue の class バインディングと衝突する)。
  //    見た目は CSS 側で [data-testid="write-review-button"] 属性セレクタ経由で当てる。
  //  - 投稿フォームはクリック時に Vue が動的マウントするモーダルで、マウント先が
  //    ウィジェットルート直下の可能性がある。よってルートは display:none にせず、
  //    data-cr-adopted 属性を付けて CSS で「中身(header-and-body)だけ」を隠す。

  var WRITE_BTN_SELECTOR = '[data-testid="write-review-button"]';
  var WIDGET_ROOT_SELECTOR = '[data-widget-name="review_widget"], .jdgm-widget';
  var MORE_WRAP_SELECTOR = '.cr-more-wrap';
  var ADOPT_TIMEOUT_MS = 20000;
  var ADOPT_OBSERVER_OPTIONS = { childList: true, subtree: true };

  // ページ内で1個だけ動かす MutationObserver(Vue の遅延描画・再描画待ち)。
  var adoptObserver = null;
  var adoptTimer = null;
  // 取り込んだボタンの復帰情報(section:unload でウィジェット内へ戻すため)。
  var adoptState = null;

  /** el から祖先方向へ selector に一致する要素を探す(closest 非対応環境のフォールバック付き)。 */
  function closestEl(el, selector) {
    var node = el;
    if (node && typeof node.closest === 'function') {
      return node.closest(selector);
    }
    while (node && node.nodeType === 1) {
      var matches = node.matches || node.msMatchesSelector || node.webkitMatchesSelector;
      if (matches && matches.call(node, selector)) {
        return node;
      }
      node = node.parentElement || node.parentNode;
    }
    return null;
  }

  /** そのボタンが既に何らかの .cr-more-wrap 配下にあるか(冪等ガードの中核)。 */
  function isAdoptedButton(btn) {
    return !!closestEl(btn, MORE_WRAP_SELECTOR);
  }

  function pauseAdoptObserver() {
    if (adoptObserver) {
      adoptObserver.disconnect();
    }
  }

  function resumeAdoptObserver() {
    if (adoptObserver && document.body) {
      adoptObserver.observe(document.body, ADOPT_OBSERVER_OPTIONS);
    }
  }

  /**
   * 「レビューを書く」ボタンの実ノードを wrap 内(a.cr-more の直前)へ移動する。
   * 既に .cr-more-wrap 配下にあるボタンしか無ければ何もしない(冪等)。
   * @returns {boolean} 取り込み済み(今回移動した/既に移動済み)なら true
   */
  function moveWriteButton(wrap) {
    if (!wrap) {
      return false;
    }
    var buttons = document.querySelectorAll(WRITE_BTN_SELECTOR);
    if (buttons.length === 0) {
      return false;
    }

    var i;
    var target = null;
    for (i = 0; i < buttons.length; i++) {
      if (!isAdoptedButton(buttons[i])) {
        target = buttons[i];
        break;
      }
    }
    if (!target) {
      return true; // 全て取り込み済み。DOM を触らない(observer の無限ループ防止)。
    }

    // 移動するとウィジェットルートを辿れなくなるため、先に取得して保持する。
    var widgetRoot = closestEl(target, WIDGET_ROOT_SELECTOR);
    var homeParent = target.parentNode;
    var homeNext = target.nextSibling;

    // 自分の DOM 変更で observer が再入しないよう、移動中は監視を止める。
    pauseAdoptObserver();
    try {
      var moreLink = wrap.querySelector('a.cr-more');
      if (moreLink) {
        wrap.insertBefore(target, moreLink);
      } else {
        wrap.appendChild(target);
      }

      // Vue の再描画で新しいボタンが生えた場合、先に取り込んだ旧ノードは
      // Vue から切り離されて機能しないため取り除く(ボタンの二重表示も防ぐ)。
      for (i = 0; i < buttons.length; i++) {
        var stale = buttons[i];
        if (stale !== target && stale.parentNode && isAdoptedButton(stale)) {
          stale.parentNode.removeChild(stale);
        }
      }

      wrap.classList.add('cr-more-wrap--with-write');
      if (widgetRoot && typeof widgetRoot.setAttribute === 'function') {
        widgetRoot.setAttribute('data-cr-adopted', 'true');
      }
    } finally {
      resumeAdoptObserver();
    }

    adoptState = {
      button: target,
      parent: homeParent,
      next: homeNext,
      widgetRoot: widgetRoot,
      wrap: wrap
    };
    return true;
  }

  /** root 配下に取り込み済みボタンがある場合だけ復帰させる(複数セクション対応)。 */
  function releaseWriteButtonIfInside(root) {
    if (!adoptState || !root || typeof root.contains !== 'function') {
      return;
    }
    if (!root.contains(adoptState.button)) {
      return;
    }
    releaseWriteButton();
  }

  /** 取り込んだボタンを Judge.me ウィジェット内の元の位置へ戻す(section:unload 用)。 */
  function releaseWriteButton() {
    var st = adoptState;
    adoptState = null;
    if (!st) {
      return;
    }
    pauseAdoptObserver();
    try {
      if (st.button && st.parent) {
        if (st.next && st.next.parentNode === st.parent) {
          st.parent.insertBefore(st.button, st.next);
        } else {
          st.parent.appendChild(st.button);
        }
      }
      if (st.wrap && st.wrap.classList) {
        st.wrap.classList.remove('cr-more-wrap--with-write');
      }
      if (st.widgetRoot && typeof st.widgetRoot.removeAttribute === 'function') {
        st.widgetRoot.removeAttribute('data-cr-adopted');
      }
    } finally {
      resumeAdoptObserver();
    }
  }

  /**
   * 取り込み先の .cr-more-wrap を1つ返す(ページ内で最初のもの)。
   * excludeRoot 配下は対象外(section:unload で消えるセクションを除くため)。
   */
  function findAdoptTarget(excludeRoot) {
    var wraps = document.querySelectorAll('.custom-reviews ' + MORE_WRAP_SELECTOR);
    for (var i = 0; i < wraps.length; i++) {
      if (!excludeRoot || typeof excludeRoot.contains !== 'function' ||
          !excludeRoot.contains(wraps[i])) {
        return wraps[i];
      }
    }
    return null;
  }

  /** 取り込み先を1つ選んで取り込みを試みる(observer コールバックの本体)。 */
  function runAdoption(excludeRoot) {
    return moveWriteButton(findAdoptTarget(excludeRoot));
  }

  /** 取り込み用 observer を停止(タイムアウト / section:unload)。 */
  function stopAdoptObserver() {
    if (adoptTimer) {
      clearTimeout(adoptTimer);
      adoptTimer = null;
    }
    if (adoptObserver) {
      adoptObserver.disconnect();
      adoptObserver = null;
    }
  }

  /**
   * Vue の遅延描画・再描画を待つ observer をページに1個だけ立てる。
   * 取り込み成功後も「元の位置に新しいボタンが現れたら再度移動する」ために生かし、
   * 常駐リスナーを残さないよう ADOPT_TIMEOUT_MS で必ず disconnect する。
   */
  function ensureAdoptObserver() {
    if (adoptObserver || typeof MutationObserver === 'undefined' || !document.body) {
      return;
    }
    adoptObserver = new MutationObserver(function () {
      runAdoption();
    });
    adoptObserver.observe(document.body, ADOPT_OBSERVER_OPTIONS);
    adoptTimer = setTimeout(stopAdoptObserver, ADOPT_TIMEOUT_MS);
  }

  /** セクション初期化時のエントリポイント。もっと見る非表示設定なら何もしない。 */
  function adoptWriteReviewButton(section) {
    var wrap = section.querySelector(MORE_WRAP_SELECTOR);
    if (!wrap) {
      return;
    }
    moveWriteButton(wrap); // ウィジェットが描画済みなら即取り込み
    ensureAdoptObserver(); // まだなら出現を待つ(既に取り込み済みでも再描画に備える)
  }

  // ---- 初期化 ------------------------------------------------------------

  /** 遅延 fetch の設定(lazy=false / IO 非対応は即時)。 */
  function setupFetch(section) {
    var lazy = section.getAttribute('data-cr-lazy') !== 'false';

    if (!lazy || typeof IntersectionObserver === 'undefined') {
      loadSection(section);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          observer.disconnect();
          loadSection(section);
          return;
        }
      }
    }, { rootMargin: IO_ROOT_MARGIN });

    observer.observe(section);
  }

  /** セクション 1 つを初期化(二重初期化ガード付き)。 */
  function initSection(section) {
    if (!section || section.__crInitialized) {
      return;
    }
    section.__crInitialized = true;
    setupClampResize(section);
    setupFetch(section);
    adoptWriteReviewButton(section);
  }

  /** 全セクションを走査して初期化。 */
  function boot() {
    var sections = document.querySelectorAll('.custom-reviews');
    for (var i = 0; i < sections.length; i++) {
      initSection(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // テーマエディタでの追加/再読込に対応(該当セクションのみ再初期化)。
  document.addEventListener('shopify:section:load', function (event) {
    var target = event && event.target;
    if (!target || typeof target.querySelectorAll !== 'function') {
      return;
    }
    // 再初期化時に開いたままのライトボックスがあれば閉じる(共有インスタンスは残す)。
    closeLightboxIfOpen();
    var sections = target.querySelectorAll('.custom-reviews');
    for (var i = 0; i < sections.length; i++) {
      initSection(sections[i]);
      // 再読込で同一ノードが返る場合 initSection は二重初期化ガードで抜けるため、
      // 「レビューを書く」ボタンの取り込みだけは毎回試みる(処理自体は冪等)。
      adoptWriteReviewButton(sections[i]);
    }
  });

  // セクション削除時: 開いていればライトボックスを閉じ、resize リスナーを解除する。
  document.addEventListener('shopify:section:unload', function (event) {
    var target = event && event.target;
    if (!target || typeof target.querySelectorAll !== 'function') {
      return;
    }
    closeLightboxIfOpen();
    var sections = target.querySelectorAll('.custom-reviews');
    if (sections.length > 0) {
      // 取り込んだ「レビューを書く」ボタンはセクションごと DOM から消えてしまうため、
      // Judge.me ウィジェット内の元の位置へ戻す(テーマエディタでの再描画時に
      // ボタンが失われないようにする)。
      releaseWriteButtonIfInside(target);
      if (findAdoptTarget(target)) {
        // 他の custom-reviews セクションが残っていれば、そちらへ引き継ぐ。
        runAdoption(target);
      } else {
        // 取り込み先が無くなったので observer をページに残さない。
        stopAdoptObserver();
      }
    }
    for (var i = 0; i < sections.length; i++) {
      teardownClampResize(sections[i]);
    }
  });
}());
